import { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Cohort } from '~/shared/constants/commonConstants'
import { PageId } from '~/shared/constants/pagesConstants'
import { PROGRESS_SECTIONS_BY_COHORT } from '~/shared/constants/progressSections'
import {
  selectCohortID,
  selectCurrentVariantSteps,
} from '~/shared/store/common/selects'
import { IStep } from '~/shared/types/variantTypes'

export interface IProgressSection {
  label: string
  value: number
  max: number
}

interface PageInfoContextProps {
  steps: IStep[]
  currentPageId: PageId
  currentPageIndex: number
  hasHeader: boolean
  hasProgressbar: boolean
  hasBurgerMenu: boolean
  currentSubscriptionPageId: PageId
  currentPaymentPageId: PageId
  currentUpsellPageId: PageId
  isPaymentPage: boolean
  isSecondPage: boolean
  pagesWithProgressBar: IStep[]
  progressSections: IProgressSection[] | null
  currentSectionLabel: string | null
}

const PageInfoContext = createContext<PageInfoContextProps | null>(null)

export const PageInfoProvider = ({ children }) => {
  const { pathname } = useLocation()
  const steps = useSelector(selectCurrentVariantSteps)

  const currentPageId = pathname.slice(1) as PageId

  const currentPageIndex = steps.findIndex(({ id }) => id === currentPageId)

  const { hasHeader, hasProgressbar, hasBurgerMenu } = {
    hasHeader:
      steps[currentPageIndex]?.hasHeader ||
      currentPageId === PageId.DOWNLOAD ||
      currentPageId === PageId.LOGIN,
    hasProgressbar: steps[currentPageIndex]?.hasProgressBar,
    hasBurgerMenu: !currentPageIndex,
  }

  let currentSubscriptionPageId = '' as PageId
  let currentPaymentPageId = '' as PageId
  let currentUpsellPageId = '' as PageId

  steps.forEach(({ isSubscriptions, isPayment, isUpsell, id }) => {
    if (isSubscriptions && !currentSubscriptionPageId) {
      currentSubscriptionPageId = id
    }

    if (isPayment && !currentPaymentPageId) currentPaymentPageId = id
    if (isUpsell && !currentUpsellPageId) currentUpsellPageId = id
  })

  const pagesWithProgressBar = useMemo(
    () => steps.filter((step) => step.hasProgressBar),
    [steps],
  )

  const cohort = useSelector(selectCohortID)

  const currentSectionLabel = useMemo<string | null>(() => {
    const sectionMap = PROGRESS_SECTIONS_BY_COHORT[cohort as Cohort]
    if (!sectionMap) return null

    return (
      Object.entries(sectionMap).find(([, pageIds]) =>
        pageIds.includes(currentPageId),
      )?.[0] ?? null
    )
  }, [cohort, currentPageId])

  const progressSections = useMemo<IProgressSection[] | null>(() => {
    const sectionMap = PROGRESS_SECTIONS_BY_COHORT[cohort as Cohort]
    if (!sectionMap) return null

    const entries = Object.entries(sectionMap)
    const currentIdxOverall = pagesWithProgressBar.findIndex(
      ({ id }) => id === currentPageId,
    )

    return entries.map(([label, pageIds]) => {
      const sectionPages = pagesWithProgressBar.filter(({ id }) =>
        pageIds.includes(id),
      )
      const max = sectionPages.length
      if (!max) return { label, value: 0, max: 1 }

      const lastSectionIdx = pagesWithProgressBar.reduce(
        (acc, { id }, idx) => (pageIds.includes(id) ? idx : acc),
        -1,
      )

      if (currentIdxOverall > lastSectionIdx) return { label, value: max, max }

      const currentIdxInSection = sectionPages.findIndex(
        ({ id }) => id === currentPageId,
      )
      if (currentIdxInSection < 0) return { label, value: 0, max }

      return { label, value: currentIdxInSection + 1, max }
    })
  }, [cohort, pagesWithProgressBar, currentPageId])

  const isPaymentPage = currentPaymentPageId === currentPageId

  const isSecondPage = steps.findIndex(({ id }) => id === currentPageId) === 1

  const value = useMemo(
    () => ({
      currentPageId,
      pagesWithProgressBar,
      progressSections,
      currentSectionLabel,
      currentPageIndex,
      hasHeader,
      hasProgressbar,
      hasBurgerMenu,
      currentSubscriptionPageId,
      currentPaymentPageId,
      currentUpsellPageId,
      isPaymentPage,
      isSecondPage,
      steps,
    }),
    [
      currentPageId,
      currentPageIndex,
      hasHeader,
      hasProgressbar,
      pagesWithProgressBar,
      progressSections,
      currentSectionLabel,
      hasBurgerMenu,
      currentSubscriptionPageId,
      currentPaymentPageId,
      currentUpsellPageId,
      isPaymentPage,
      isSecondPage,
      steps,
    ],
  )

  return (
    <PageInfoContext.Provider value={value}>
      {children}
    </PageInfoContext.Provider>
  )
}

export const usePageInfoContext = () => {
  const context = useContext(PageInfoContext)
  if (!context) {
    throw new Error('usePageInfoContext must be used within a PageInfoProvider')
  }
  return context
}
